import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAction, useMutation } from "convex/react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { ObservedSpeaker, SessionState, TranscriptEntry } from "../../shared/types.ts";
import type { RoleAuth } from "../lib/auth.ts";
import { fmtTime, speakerColor } from "../lib/format.ts";
import { notificationCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import { isTranscriptAtBottom } from "../lib/transcriptScroll.ts";
import {
  appendTranscriptEntriesToGroupingState,
  contiguousEntryRange,
  createTranscriptGroupingState,
  type TranscriptGroupingOptions,
  type TranscriptGroupingState,
} from "../lib/transcript.ts";
import type { SessionTranscriptFeed } from "../lib/useSessionTranscript.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { compareObservedSpeakers, projectTranscriptSpeakerNames } from "./sessionSpeakerModel.ts";

type Status = SessionState["status"];
type LineState = "processed" | "processing" | "pending";
type DraftCorrection = {
  textOverride?: string;
  speakerKeyOverride?: string | null;
};

const SEGMENT_DRAG_CLICK_THRESHOLD_PX = 4;
const TRANSCRIPT_RENDER_WINDOW_GROUPS = 180;
const TRANSCRIPT_RENDER_OVERSCAN_GROUPS = 40;
const TRANSCRIPT_GROUP_ESTIMATED_HEIGHT_PX = 46;

// Classify a transcript line against the generation high-water marks so the
// admin can see what the model has already read, what it's reading now, and
// what's still queued.
function lineState(ts: number, s: Status): LineState {
  if (s.processedThroughTs != null && ts <= s.processedThroughTs) return "processed";
  if (s.generating && s.processingThroughTs != null && ts <= s.processingThroughTs) return "processing";
  return "pending";
}

function groupState(entries: TranscriptEntry[], s: Status): LineState {
  let hasPending = false;
  for (const entry of entries) {
    const state = lineState(entry.ts, s);
    if (state === "processing") return "processing";
    if (state === "pending") hasPending = true;
  }
  return hasPending ? "pending" : "processed";
}

function shouldIgnoreSegmentClick(
  event: React.MouseEvent,
  entryId: string,
  pointerDown: { entryId: string; x: number; y: number } | null,
): boolean {
  if (hasSelectedTextInside(event.currentTarget as HTMLElement)) return true;
  if (!pointerDown || pointerDown.entryId !== entryId) return false;
  const dx = event.clientX - pointerDown.x;
  const dy = event.clientY - pointerDown.y;
  return Math.hypot(dx, dy) > SEGMENT_DRAG_CLICK_THRESHOLD_PX;
}

function hasSelectedTextInside(element: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.toString().trim() === "") return false;
  for (let index = 0; index < selection.rangeCount; index += 1) {
    const range = selection.getRangeAt(index);
    try {
      if (range.intersectsNode(element)) return true;
    } catch {
      return true;
    }
  }
  return false;
}

/** Admin transcript as a collapsible right sidebar, with the one-time speaker
 *  mapping folded in at the top (it's transcript metadata). The transcript
 *  feed is owned by the dashboard (which also drives the function bar) and
 *  passed in so transcript history is loaded once for the admin surface. */
export function TranscriptPanel({
  interim,
  status,
  sessionId,
  auth,
  speakers,
  feed,
  open,
  onToggle,
}: {
  interim: string;
  status: Status;
  sessionId: string;
  auth: RoleAuth;
  speakers: ObservedSpeaker[];
  feed: SessionTranscriptFeed;
  open: boolean;
  onToggle: () => void;
}) {
  const applyTranscriptCorrectionsBatch = useMutation(convexApi.transcriptCorrections.applyTranscriptCorrectionsBatch);
  const repairPendingTranscriptEdits = useAction(convexApi.panelRecapGeneration.repairPendingTranscriptEdits);
  const transcriptFeed = feed;
  const entries = transcriptFeed.entries;
  const speakersByKey = useMemo(() => speakerLookup(speakers), [speakers]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const followTailRef = useRef(true);
  const initialScrollSessionRef = useRef<string | null>(null);
  const lastScrollTopRef = useRef(0);
  const previousOpenRef = useRef(open);
  const [draftCorrectionsByEntryId, setDraftCorrectionsByEntryId] = useState<Map<string, DraftCorrection>>(
    () => new Map(),
  );
  const [draftError, setDraftError] = useState<string | null>(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [anchorEntryId, setAnchorEntryId] = useState<string | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<string[]>([]);
  const [rangeSpeakerKey, setRangeSpeakerKey] = useState<string>("");
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairError, setRepairError] = useState<string | null>(null);
  const [renderStart, setRenderStart] = useState(0);
  const segmentPointerDownRef = useRef<{ entryId: string; x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    initialScrollSessionRef.current = null;
    followTailRef.current = true;
    lastScrollTopRef.current = 0;
    previousOpenRef.current = open;
    setRenderStart(0);
  }, [sessionId]);

  const displayEntries = useMemo(
    () => projectTranscriptSpeakerNames(
      entries.map((entry) =>
        attachObservedCapture(
          applyDraftCorrection(entry, draftCorrectionsByEntryId.get(entry.id), speakers),
          speakersByKey,
        ),
      ),
      speakers,
    ),
    [draftCorrectionsByEntryId, entries, speakers, speakersByKey],
  );
  const groupingOptions = useMemo<TranscriptGroupingOptions<TranscriptEntry>>(
    () => ({ isBoundaryBetween: isCaptureBoundary }),
    [],
  );
  const groupingResetKey = useMemo(
    () => [
      transcriptFeed.projectionVersion,
      observedSpeakerSignature(speakers),
      draftCorrectionSignature(draftCorrectionsByEntryId),
    ].join("::"),
    [draftCorrectionsByEntryId, speakers, transcriptFeed.projectionVersion],
  );
  const groupedEntries = useAppendOnlyTranscriptGroups(displayEntries, groupingOptions, groupingResetKey);
  const renderWindow = useMemo(
    () => transcriptRenderWindow(groupedEntries.length, renderStart),
    [groupedEntries.length, renderStart],
  );
  useLayoutEffect(() => {
    setRenderStart((current) => {
      const maxStart = maxTranscriptRenderStart(groupedEntries.length);
      if (initialScrollSessionRef.current !== sessionId || followTailRef.current) return maxStart;
      return Math.min(current, maxStart);
    });
  }, [groupedEntries.length, sessionId]);
  useLayoutEffect(() => {
    const justOpened = open && !previousOpenRef.current;
    previousOpenRef.current = open;
    if (!open || transcriptFeed.loading || transcriptFeed.sessionId !== sessionId) return;

    const el = scrollRef.current;
    const hasContent = groupedEntries.length > 0 || interim.length > 0;
    if (!el || !hasContent) return;

    const needsInitialScroll = initialScrollSessionRef.current !== sessionId;
    const maxStart = maxTranscriptRenderStart(groupedEntries.length);
    if ((needsInitialScroll || followTailRef.current) && renderStart !== maxStart) return;

    if (needsInitialScroll || followTailRef.current) {
      scrollTranscriptToBottom(el);
      lastScrollTopRef.current = el.scrollTop;
      initialScrollSessionRef.current = sessionId;
      return;
    }

    if (justOpened) el.scrollTop = lastScrollTopRef.current;
  }, [
    entries.length,
    groupedEntries.length,
    interim,
    open,
    renderStart,
    sessionId,
    transcriptFeed.loading,
    transcriptFeed.sessionId,
  ]);
  const flatEntryIds = useMemo(
    () => groupedEntries.flatMap((group) => group.inlineParts.map((part) => part.entry.id)),
    [groupedEntries],
  );
  const selectedEntryIdSet = useMemo(() => new Set(selectedEntryIds), [selectedEntryIds]);
  const draftCorrectionCount = draftCorrectionsByEntryId.size;
  const repairPendingCount = status.repairPendingRecapCount ?? 0;
  useEffect(() => {
    if (anchorEntryId && !flatEntryIds.includes(anchorEntryId)) setAnchorEntryId(null);
    setSelectedEntryIds((current) => current.filter((entryId) => flatEntryIds.includes(entryId)));
  }, [anchorEntryId, flatEntryIds]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setSelectedEntryIds([]);
        setAnchorEntryId(null);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function onTranscriptBackgroundClick(event: React.MouseEvent) {
    if ((event.target as HTMLElement).closest(".t-segment")) return;
    setSelectedEntryIds([]);
    setAnchorEntryId(null);
  }

  function onTranscriptScroll(event: React.UIEvent<HTMLDivElement>) {
    const el = event.currentTarget;
    lastScrollTopRef.current = el.scrollTop;
    followTailRef.current = isTranscriptAtBottom(el);
    const nextStart = transcriptRenderStartForScroll(el.scrollTop, groupedEntries.length);
    if (Math.abs(nextStart - renderStart) >= TRANSCRIPT_RENDER_OVERSCAN_GROUPS / 2) {
      setRenderStart(nextStart);
    }
  }

  function onSegmentClick(entry: TranscriptEntry, shiftKey: boolean) {
    if (shiftKey && anchorEntryId) {
      const range = contiguousEntryRange(flatEntryIds, anchorEntryId, entry.id);
      if (range.length > 0) {
        setSelectedEntryIds(range);
        setRangeSpeakerKey("");
        return;
      }
    }
    setAnchorEntryId(entry.id);
    setSelectedEntryIds([entry.id]);
  }

  function onSegmentPointerDown(event: React.PointerEvent, entry: TranscriptEntry) {
    segmentPointerDownRef.current = {
      entryId: entry.id,
      x: event.clientX,
      y: event.clientY,
    };
    if (event.shiftKey) event.preventDefault();
  }

  function onSegmentMouseDown(event: React.MouseEvent, entry: TranscriptEntry) {
    segmentPointerDownRef.current = {
      entryId: entry.id,
      x: event.clientX,
      y: event.clientY,
    };
    if (event.shiftKey) event.preventDefault();
  }

  function onSegmentClickGuarded(event: React.MouseEvent, entry: TranscriptEntry) {
    if (shouldIgnoreSegmentClick(event, entry.id, segmentPointerDownRef.current)) {
      segmentPointerDownRef.current = null;
      return;
    }
    segmentPointerDownRef.current = null;
    onSegmentClick(entry, event.shiftKey);
  }

  function stageRangeSpeaker() {
    if (selectedEntryIds.length === 0 || !rangeSpeakerKey) return;
    setDraftCorrectionsByEntryId((current) => {
      const next = new Map(current);
      for (const entryId of selectedEntryIds) {
        next.set(entryId, {
          ...next.get(entryId),
          speakerKeyOverride: rangeSpeakerKey,
        });
      }
      return next;
    });
    setDraftError(null);
    setSelectedEntryIds([]);
  }

  function cancelRangeSelection() {
    setSelectedEntryIds([]);
    setRangeSpeakerKey("");
  }

  function discardDraftCorrections() {
    setDraftCorrectionsByEntryId(new Map());
    setDraftError(null);
    setSelectedEntryIds([]);
  }

  async function applyDraftCorrections() {
    const corrections = Array.from(draftCorrectionsByEntryId.entries()).map(([entryId, draft]) => {
      const correction: {
        transcriptEntryId: Id<"transcriptEntries">;
        textOverride?: string;
        speakerKeyOverride?: string | null;
      } = {
        transcriptEntryId: entryId as Id<"transcriptEntries">,
      };
      if (hasDraftField(draft, "textOverride")) correction.textOverride = draft.textOverride;
      if (hasDraftField(draft, "speakerKeyOverride")) correction.speakerKeyOverride = draft.speakerKeyOverride;
      return correction;
    });
    if (corrections.length === 0) return;

    setApplyBusy(true);
    setDraftError(null);
    try {
      await applyTranscriptCorrectionsBatch({
        auth,
        sessionId: sessionId as Id<"sessions">,
        corrections,
      });
      setDraftCorrectionsByEntryId(new Map());
      setSelectedEntryIds([]);
      notify.success(notificationCopy.admin.correctionsApplied);
    } catch (error) {
      setDraftError(errorMessage(error));
    } finally {
      setApplyBusy(false);
    }
  }

  async function repairPendingSummaries() {
    setRepairBusy(true);
    setRepairError(null);
    try {
      const result = await repairPendingTranscriptEdits({
        auth,
        sessionId: sessionId as Id<"sessions">,
      });
      if (result.remaining > 0 && result.reason) {
        setRepairError(result.reason);
      } else {
        notify.success(notificationCopy.admin.summariesRepaired);
      }
    } catch (error) {
      setRepairError(errorMessage(error));
    } finally {
      setRepairBusy(false);
    }
  }

  return (
    <aside className={`mod-transcript ${open ? "" : "collapsed"}`} data-testid="transcript-panel">
      <div className="mod-transcript-head">
        {open && (
          <h2>Transcript</h2>
        )}
        <button
          type="button"
          className="btn ghost sm"
          data-testid="transcript-collapse"
          aria-label={open ? "Collapse transcript" : "Expand transcript"}
          onClick={onToggle}
        >
          {open ? <CaretRight weight="bold" /> : <><CaretLeft weight="bold" /> transcript</>}
        </button>
      </div>
      {open && (
        <>
          {draftCorrectionCount > 0 && (
            <div className="transcript-draft-bar" data-testid="transcript-draft-bar">
              <span>{draftCorrectionCount} pending correction{draftCorrectionCount === 1 ? "" : "s"}</span>
              <div className="transcript-draft-actions">
                <button type="button" className="btn ghost xs" onClick={discardDraftCorrections} disabled={applyBusy}>
                  Discard
                </button>
                <button
                  type="button"
                  className="btn primary xs"
                  onClick={() => void applyDraftCorrections()}
                  disabled={applyBusy}
                  data-testid="transcript-apply-corrections"
                >
                  {applyBusy ? "Applying..." : "Apply corrections"}
                </button>
              </div>
              {draftError && <div className="transcript-draft-error" role="alert">{draftError}</div>}
            </div>
          )}
          {selectedEntryIds.length === 1 && (() => {
            const selectedEntry = displayEntries.find((e) => e.id === selectedEntryIds[0]);
            if (!selectedEntry) return null;
            return (
              <InlineSegmentEditor
                entry={selectedEntry}
                speakers={speakers}
                onStage={(input) => {
                  setDraftCorrectionsByEntryId((current) => {
                    const next = new Map(current);
                    next.set(selectedEntry.id, {
                      ...next.get(selectedEntry.id),
                      ...(input.speakerKeyOverride !== undefined
                        ? { speakerKeyOverride: input.speakerKeyOverride }
                        : {}),
                      ...(input.textOverride !== undefined
                        ? { textOverride: input.textOverride }
                        : {}),
                    });
                    return next;
                  });
                  setDraftError(null);
                  setSelectedEntryIds([]);
                }}
                onCancel={() => setSelectedEntryIds([])}
              />
            );
          })()}
          {selectedEntryIds.length > 1 && (
            <div className="transcript-range-bar" data-testid="transcript-range-bar">
              <span>{selectedEntryIds.length} selected</span>
              <select
                value={rangeSpeakerKey}
                onChange={(event) => setRangeSpeakerKey(event.target.value)}
                data-testid="transcript-range-speaker"
              >
                <option value="" disabled>Speaker</option>
                {speakerOptions(speakers).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="transcript-range-actions">
                <button type="button" className="btn ghost xs" onClick={cancelRangeSelection}>
                  Cancel
                </button>
                <button type="button" className="btn primary xs" onClick={stageRangeSpeaker} disabled={!rangeSpeakerKey}>
                  Reassign
                </button>
              </div>
            </div>
          )}
          {repairPendingCount > 0 && (
            <div className="transcript-repair-banner" data-testid="transcript-repair-banner">
              <span>
                Transcript corrections affect {repairPendingCount} summar{repairPendingCount === 1 ? "y" : "ies"}.
              </span>
              <button
                type="button"
                className="btn primary xs"
                onClick={() => void repairPendingSummaries()}
                disabled={repairBusy || status.repairingFromTs != null}
                data-testid="transcript-repair-summaries"
              >
                {repairBusy || status.repairingFromTs != null ? "Repairing..." : "Repair touched summaries"}
              </button>
              {repairError && <div className="transcript-draft-error" role="alert">{repairError}</div>}
            </div>
          )}
          <div
            ref={scrollRef}
            className="mod-transcript-scroll transcript-scroll"
            onClick={onTranscriptBackgroundClick}
            onScroll={onTranscriptScroll}
          >
            {transcriptFeed.loading && (
              <EmptyState size="sm">Loading transcript history...</EmptyState>
            )}
            {!transcriptFeed.loading && transcriptFeed.error && (
              <EmptyState size="sm">{transcriptFeed.error}</EmptyState>
            )}
            {!transcriptFeed.loading && !transcriptFeed.error && entries.length === 0 && !interim && (
              <EmptyState size="sm">No transcript yet. Start recording, add a manual line, or inject the sample.</EmptyState>
            )}
            {renderWindow.topSpacerHeight > 0 && (
              <div
                className="transcript-window-spacer"
                style={{ height: renderWindow.topSpacerHeight }}
                aria-hidden="true"
              />
            )}
            {groupedEntries.slice(renderWindow.start, renderWindow.end).map((group, windowIndex) => {
              const index = renderWindow.start + windowIndex;
              const st = groupState(group.entries, status);
              const firstEntry = group.entries[0];
              const previousEntry = groupedEntries[index - 1]?.entries.at(-1);
              const captureDivider = captureDividerLabel(previousEntry, firstEntry);
              const displaySpeaker = transcriptSpeakerLabel(
                group.speakerKey,
                group.speaker,
                firstEntry,
                speakersByKey,
              );
              return (
                <div key={group.id}>
                  {captureDivider && (
                    <div
                      className="transcript-capture-divider"
                      data-testid="transcript-capture-divider"
                      data-capture-run={firstEntry?.capture?.captureRunLabel ?? undefined}
                    >
                      {captureDivider}
                    </div>
                  )}
                  <div
                    className={`t-entry t-${st}`}
                    data-testid="transcript-entry"
                    data-source={group.entries.at(-1)?.source}
                    data-proc={st}
                    data-entry-count={group.entries.length}
                    data-speaker-keys={group.entries.map((entry) => entry.speakerKey).filter(Boolean).join(" ")}
                    data-capture-run={firstEntry?.capture?.captureRunLabel ?? undefined}
                  >
                    <span className="t-time">{fmtTime(group.ts)}</span>
                    <span
                      className={`t-speaker src-${group.entries.at(-1)?.source}`}
                      style={{ color: speakerColor(group.speaker, group.colorSeed) }}
                      data-speaker={displaySpeaker}
                      data-speaker-key={group.speakerKey ?? undefined}
                    >
                      {displaySpeaker}
                    </span>
                    <span className="t-text">
                      {group.inlineParts.map((part) => (
                        <span
                          key={part.entry.id}
                          role="button"
                          tabIndex={0}
                          className={[
                            "t-segment",
                            part.entry.corrected ? "corrected" : "",
                            draftCorrectionsByEntryId.has(part.entry.id) ? "drafted" : "",
                            selectedEntryIdSet.has(part.entry.id) ? "selected" : "",
                          ].filter(Boolean).join(" ")}
                          data-testid="transcript-segment"
                          data-entry-id={part.entry.id}
                          onPointerDown={(event) => onSegmentPointerDown(event, part.entry)}
                          onMouseDown={(event) => onSegmentMouseDown(event, part.entry)}
                          onClick={(event) => onSegmentClickGuarded(event, part.entry)}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            onSegmentClick(part.entry, event.shiftKey);
                          }}
                        >
                          {part.prefix}
                          {part.entry.text}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
              );
            })}
            {renderWindow.bottomSpacerHeight > 0 && (
              <div
                className="transcript-window-spacer"
                style={{ height: renderWindow.bottomSpacerHeight }}
                aria-hidden="true"
              />
            )}
            {interim && (
              <div className="t-entry interim" data-testid="transcript-interim">
                <span className="t-time">…</span>
                <span className="t-speaker src-mic">Mic</span>
                <span className="t-text">{interim}</span>
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

function useAppendOnlyTranscriptGroups(
  entries: TranscriptEntry[],
  options: TranscriptGroupingOptions<TranscriptEntry>,
  resetKey: string,
) {
  const previousRef = useRef<{
    entries: TranscriptEntry[];
    resetKey: string;
    state: TranscriptGroupingState<TranscriptEntry>;
  } | null>(null);

  return useMemo(() => {
    const previous = previousRef.current;
    const state = previous &&
      previous.resetKey === resetKey &&
      isAppendOnlyTranscriptUpdate(previous.entries, entries)
        ? appendTranscriptEntriesToGroupingState(
            previous.state,
            entries.slice(previous.entries.length),
            options,
          )
        : createTranscriptGroupingState(entries, options);

    previousRef.current = { entries, resetKey, state };
    return state.groups;
  }, [entries, options, resetKey]);
}

function isAppendOnlyTranscriptUpdate(previous: TranscriptEntry[], next: TranscriptEntry[]): boolean {
  if (previous.length > next.length) return false;
  for (let index = 0; index < previous.length; index += 1) {
    if (!sameTranscriptProjection(previous[index], next[index])) return false;
  }
  return true;
}

function sameTranscriptProjection(left: TranscriptEntry, right: TranscriptEntry): boolean {
  return left.id === right.id &&
    left.ts === right.ts &&
    left.speaker === right.speaker &&
    left.speakerKey === right.speakerKey &&
    left.text === right.text &&
    left.source === right.source &&
    left.final === right.final &&
    left.corrected === right.corrected &&
    captureRunKey(left) === captureRunKey(right);
}

function observedSpeakerSignature(speakers: ObservedSpeaker[]): string {
  return speakers.map((speaker) => [
    speaker.speakerKey,
    speaker.provisionalSpeaker,
    speaker.displayName ?? "",
    speaker.capture?.captureStreamId ?? "",
    speaker.capture?.compactLabel ?? "",
  ].join("|")).join(";");
}

function draftCorrectionSignature(drafts: Map<string, DraftCorrection>): string {
  if (drafts.size === 0) return "";
  return Array.from(drafts.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([entryId, draft]) => [
      entryId,
      draft.speakerKeyOverride ?? "",
      draft.textOverride ?? "",
    ].join("|"))
    .join(";");
}

function transcriptRenderWindow(totalGroups: number, renderStart: number) {
  const start = clamp(renderStart, 0, maxTranscriptRenderStart(totalGroups));
  const end = Math.min(totalGroups, start + TRANSCRIPT_RENDER_WINDOW_GROUPS);
  return {
    start,
    end,
    topSpacerHeight: start * TRANSCRIPT_GROUP_ESTIMATED_HEIGHT_PX,
    bottomSpacerHeight: Math.max(0, totalGroups - end) * TRANSCRIPT_GROUP_ESTIMATED_HEIGHT_PX,
  };
}

function transcriptRenderStartForScroll(scrollTop: number, totalGroups: number): number {
  const estimatedStart = Math.floor(scrollTop / TRANSCRIPT_GROUP_ESTIMATED_HEIGHT_PX) -
    TRANSCRIPT_RENDER_OVERSCAN_GROUPS;
  return clamp(estimatedStart, 0, maxTranscriptRenderStart(totalGroups));
}

function maxTranscriptRenderStart(totalGroups: number): number {
  return Math.max(0, totalGroups - TRANSCRIPT_RENDER_WINDOW_GROUPS);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function scrollTranscriptToBottom(element: HTMLDivElement): void {
  element.scrollTop = element.scrollHeight;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Could not update transcript segment";
}

function InlineSegmentEditor({
  entry,
  speakers,
  onStage,
  onCancel,
}: {
  entry: TranscriptEntry;
  speakers: ObservedSpeaker[];
  onStage: (input: { textOverride?: string; speakerKeyOverride?: string | null }) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(entry.text);
  const [speakerKey, setSpeakerKey] = useState<string | null>(entry.speakerKey ?? null);

  useEffect(() => {
    setText(entry.text);
    setSpeakerKey(entry.speakerKey ?? null);
  }, [entry.id, entry.text, entry.speakerKey]);

  const options = speakerOptions(speakers);
  const dirty = text !== entry.text || (speakerKey ?? null) !== (entry.speakerKey ?? null);
  const hasRaw = entry.rawText != null || entry.rawSpeaker != null;

  function revertToRaw() {
    setText(entry.rawText ?? entry.text);
    setSpeakerKey(entry.rawSpeakerKey ?? entry.speakerKey ?? null);
  }

  return (
    <div className="transcript-inline-editor" data-testid="transcript-inline-editor">
      <div className="transcript-inline-editor-row">
        <select
          value={speakerKey ?? ""}
          onChange={(e) => setSpeakerKey(e.target.value || null)}
          data-testid="transcript-inline-speaker"
        >
          <option value="">No speaker</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="transcript-inline-editor-actions">
          {hasRaw && (
            <button type="button" className="btn ghost xs" onClick={revertToRaw}>
              Revert
            </button>
          )}
          <button type="button" className="btn ghost xs" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary xs"
            onClick={() => onStage({ textOverride: text, speakerKeyOverride: speakerKey })}
            disabled={!dirty}
            data-testid="transcript-inline-stage"
          >
            Stage
          </button>
        </div>
      </div>
      <textarea
        className="transcript-inline-text"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="transcript-inline-text"
      />
    </div>
  );
}

function applyDraftCorrection(
  entry: TranscriptEntry,
  draft: DraftCorrection | undefined,
  speakers: ObservedSpeaker[],
): TranscriptEntry {
  if (!draft) return entry;

  const speakerKey = hasDraftField(draft, "speakerKeyOverride")
    ? draft.speakerKeyOverride ?? null
    : entry.speakerKey ?? null;
  const text = hasDraftField(draft, "textOverride")
    ? draft.textOverride?.trim() || entry.text
    : entry.text;
  return {
    ...entry,
    text,
    speakerKey,
    speaker: resolvedDraftSpeaker(entry, speakerKey, speakers),
  };
}

function resolvedDraftSpeaker(
  entry: TranscriptEntry,
  speakerKey: string | null,
  speakers: ObservedSpeaker[],
): string {
  if (!speakerKey) return entry.rawSpeaker ?? entry.speaker;
  const speaker = speakers.find((candidate) => candidate.speakerKey === speakerKey);
  return speaker?.displayName?.trim() || speaker?.provisionalSpeaker || entry.speaker;
}

function speakerOptions(speakers: ObservedSpeaker[]): Array<{ value: string; label: string }> {
  return [...speakers]
    .sort(compareObservedSpeakers)
    .map((speaker) => ({
      value: speaker.speakerKey,
      label: speakerOptionLabel(speaker),
    }));
}

function speakerOptionLabel(speaker: ObservedSpeaker): string {
  const displayLabel = speaker.displayName?.trim() || speaker.provisionalSpeaker;
  if (!speaker.capture?.compactLabel) return displayLabel;
  return `${speaker.capture.compactLabel} - ${displayLabel}`;
}

function speakerLookup(speakers: ObservedSpeaker[]): Map<string, ObservedSpeaker> {
  return new Map(speakers.map((speaker) => [speaker.speakerKey, speaker]));
}

function transcriptSpeakerLabel(
  speakerKey: string | null | undefined,
  groupedSpeaker: string,
  firstEntry: TranscriptEntry | undefined,
  speakersByKey: Map<string, ObservedSpeaker>,
): string {
  const observed = speakerKey ? speakersByKey.get(speakerKey) : null;
  const displayName = observed?.displayName?.trim();
  if (displayName) return displayName;
  return firstEntry?.capture?.compactLabel ?? groupedSpeaker;
}

function attachObservedCapture(
  entry: TranscriptEntry,
  speakersByKey: Map<string, ObservedSpeaker>,
): TranscriptEntry {
  const rawSpeakerKey = entry.rawSpeakerKey?.trim() || entry.speakerKey?.trim() || null;
  const capture = rawSpeakerKey ? speakersByKey.get(rawSpeakerKey)?.capture ?? null : null;
  return capture ? { ...entry, capture } : entry;
}

function isCaptureBoundary(previous: TranscriptEntry, next: TranscriptEntry): boolean {
  return captureRunKey(previous) !== captureRunKey(next);
}

function captureDividerLabel(
  previous: TranscriptEntry | undefined,
  next: TranscriptEntry | undefined,
): string | null {
  const nextRun = next?.capture;
  if (!nextRun?.captureRunIndex || nextRun.captureRunIndex <= 1) return null;
  if (captureRunKey(previous) === captureRunKey(next)) return null;
  return `Capture ${nextRun.captureRunIndex} started`;
}

function captureRunKey(entry: TranscriptEntry | undefined): string | null {
  return entry?.capture?.captureStreamId ?? null;
}


function hasDraftField<K extends keyof DraftCorrection>(
  draft: DraftCorrection,
  key: K,
): draft is DraftCorrection & Required<Pick<DraftCorrection, K>> {
  return Object.prototype.hasOwnProperty.call(draft, key);
}
